import PrimeReact, { addLocale, locale } from 'primereact/api'
import { ProgressSpinner } from 'primereact/progressspinner'
import React, { Suspense, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, Route, Switch } from 'react-router-dom'
import './assets/scss/App.scss'
import Layout from './components/Layout/Layout'
import api from './lib/api'
import { loadGoogleMaps, patchGMapsApi, removeGoogleMaps } from './lib/GoogleMaps'
import { HOME, LOCALE, MODULES, ROUTES } from './static/config'
import { catalogActions } from './store/catalog'
import { uiActions } from './store/ui'

const Dashboard = React.lazy(() => import('./pages/Dashboard'))
const Auth = React.lazy(() => import('./pages/Auth'))
const Vehicles = React.lazy(() => import('./pages/Vehicles'))
const EditVehicle = React.lazy(() => import('./pages/EditVehicle'))
const VehicleTrips = React.lazy(() => import('./pages/VehicleTrips'))
const Trips = React.lazy(() => import('./pages/Trips'))
const TripDetail = React.lazy(() => import('./pages/TripDetail'))
const InProgress = React.lazy(() => import('./pages/InProgress'))
const Devices = React.lazy(() => import('./pages/Devices'))
const EditDevice = React.lazy(() => import('./pages/EditDevice'))

addLocale(LOCALE.locale, LOCALE.options)
locale(LOCALE.locale)

PrimeReact.autoZIndex = true
PrimeReact.ripple = true

function App () {
  const dispatch = useDispatch()
  const { isLoggedIn } = useSelector((state) => state.auth)

  useEffect(() => {
    let isMounted = true

    loadGoogleMaps(() => {
      patchGMapsApi()
      dispatch(uiActions.setGoogleMapsReady(true))
    })

    if (isLoggedIn) {
      api.getEventsType().then(res => {
        if (isMounted) dispatch(catalogActions.setEventsType(res.data))
      })
      api.getEcoScoreDecode().then(res => {
        if (isMounted) dispatch(catalogActions.setEcoScoreDecode(res.data))
      })
    }

    return () => {
      removeGoogleMaps()
      isMounted = false
    }
  }, [dispatch, isLoggedIn])

  return (
    <Layout>
      <Suspense
        fallback={
          <div className="p-d-flex p-ai-center p-jc-center p-mb-6 p-mt-6">
            <ProgressSpinner />
          </div>
        }
      >
        <Switch>
          <Route path={ROUTES.base} exact>
            {isLoggedIn && <Redirect to={HOME.url} />}{!isLoggedIn && <Redirect to={ROUTES.auth} />}
          </Route>
          <Route path={ROUTES.auth}>
            {!isLoggedIn && <Auth />}{isLoggedIn && <Redirect to={HOME.url} />}
          </Route>

          {MODULES.dashboard.active && (
            <Route path={HOME.url}>
              {isLoggedIn && <Dashboard />}{!isLoggedIn && <Redirect to={ROUTES.base} />}
            </Route>
          )}
          {MODULES.vehicles.active && (
            <Route path={ROUTES.vehicles}>
              {isLoggedIn && <Vehicles />}{!isLoggedIn && <Redirect to={ROUTES.base} />}
            </Route>
          )}
          {MODULES.vehicles.active && (
            <Route path={ROUTES.createVehicle} exact>
              {isLoggedIn && <EditVehicle />}{!isLoggedIn && <Redirect to={ROUTES.base} />}
            </Route>
          )}
          {MODULES.vehicles.active && (
            <Route path={`${ROUTES.editVehicle}:vehicleId`}>
              {isLoggedIn && <EditVehicle />}{!isLoggedIn && <Redirect to={ROUTES.base} />}
            </Route>
          )}
          {MODULES.vehicles.active && (
            <Route path={`${ROUTES.vehicleTrips}:vehicleId`}>
              {isLoggedIn && <VehicleTrips />}{!isLoggedIn && <Redirect to={ROUTES.base} />}
            </Route>
          )}
          {MODULES.trips.active && (
            <Route path={ROUTES.trips} exact>
              {isLoggedIn && <Trips />}{!isLoggedIn && <Redirect to={ROUTES.base} />}
            </Route>
          )}
          {MODULES.trips.active && (
            <Route path={`${ROUTES.tripDetail}:tripId`}>
              {isLoggedIn && <TripDetail />}{!isLoggedIn && <Redirect to={ROUTES.base} />}
            </Route>
          )}
          {MODULES.trips.active && (
            <Route path={`${ROUTES.tripInProgress}:deviceId/:tripId/:vehicleId`}>
              {isLoggedIn && <InProgress />}{!isLoggedIn && <Redirect to={ROUTES.base} />}
            </Route>
          )}
          {MODULES.devices.active && (
            <Route path={ROUTES.devices}>
              {isLoggedIn && <Devices />}{!isLoggedIn && <Redirect to={ROUTES.base} />}
            </Route>
          )}
          {MODULES.devices.active && (
            <Route path={ROUTES.createDevice} exact>
              {isLoggedIn && <EditDevice />}{!isLoggedIn && <Redirect to={ROUTES.base} />}
            </Route>
          )}
          {MODULES.devices.active && (
            <Route path={`${ROUTES.editDevice}:deviceId`}>
              {isLoggedIn && <EditDevice />}{!isLoggedIn && <Redirect to={ROUTES.base} />}
            </Route>
          )}

          <Route path="*">
            <Redirect to={HOME.url} />
          </Route>
        </Switch>
      </Suspense>
    </Layout>
  )
}

export default App
